import httpClient from '~/lib/http-client';
import type { StorageKind } from '@shared/types/storage';

export interface StorageFile {
  key: string;
  name: string;
  size: number;
  contentType: string;
  lastModified: string;
  url: string;
}

export interface UploadResult {
  key: string;
  name: string;
  url: string;
  size: number;
  contentType: string;
}

/**
 * List all files in a chat's storage.
 */
export async function apiListStorageFiles(chatId: string): Promise<StorageFile[]> {
  const { data } = await httpClient.get<StorageFile[]>(`/api/storage/files?chatId=${encodeURIComponent(chatId)}`);
  return data;
}

/**
 * Upload a file to chat storage.
 * Returns the public URL for use in generated code.
 * `kind` selects the key prefix; only brochure kinds accept PDFs.
 * `claim` lets a new chat's first upload create/claim the chat row (same as the
 * brochure flow) so it doesn't 404 before the chat is otherwise created.
 */
export async function apiUploadStorageFile(
  chatId: string,
  file: File,
  kind?: StorageKind,
  opts?: { claim?: boolean },
): Promise<UploadResult> {
  const formData = new FormData();
  formData.append('file', file);

  const kindParam = kind ? `&kind=${encodeURIComponent(kind)}` : '';
  const claimParam = opts?.claim ? '&claim=1' : '';
  const { data } = await httpClient.post<UploadResult>(
    `/api/storage/upload?chatId=${encodeURIComponent(chatId)}${kindParam}${claimParam}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );

  return data;
}

/**
 * Delete a file from chat storage.
 */
export async function apiDeleteStorageFile(chatId: string, key: string): Promise<void> {
  await httpClient.delete(`/api/storage/files/${encodeURIComponent(key)}?chatId=${encodeURIComponent(chatId)}`);
}

/**
 * Get the public URL for a file.
 */
export async function apiGetStorageFileUrl(chatId: string, key: string): Promise<string> {
  const { data } = await httpClient.get<{ url: string }>(
    `/api/storage/files/${encodeURIComponent(key)}/url?chatId=${encodeURIComponent(chatId)}`,
  );
  return data.url;
}
